import React, { useState, useEffect } from 'react';
import { X, Edit2, Save, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LegalPagesTab = ({ getStoredPassword }) => {
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [termsOfService, setTermsOfService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeEditPage, setActiveEditPage] = useState(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [privacyRes, termsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/page-settings/privacy_policy?password=${getStoredPassword()}`),
          fetch(`${API_URL}/api/admin/page-settings/terms_of_service?password=${getStoredPassword()}`)
        ]);
        if (privacyRes.ok) setPrivacyPolicy(await privacyRes.json());
        if (termsRes.ok) setTermsOfService(await termsRes.json());
      } catch (e) {
        console.error('Failed to fetch page settings:', e);
      }
      setLoading(false);
    };
    fetchSettings();
  }, [getStoredPassword]);

  const saveSettings = async (pageName, settings) => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/page-settings/${pageName}?password=${getStoredPassword()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings)
        }
      );
      if (res.ok) {
        toast.success(`${pageName === 'privacy_policy' ? 'Privacy Policy' : 'Terms of Service'} saved`);
        const data = await res.json();
        if (pageName === 'privacy_policy') setPrivacyPolicy(data);
        else setTermsOfService(data);
      } else {
        toast.error('Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const updateSection = (pageType, sectionIndex, field, value) => {
    if (pageType === 'privacy') {
      const updated = {...privacyPolicy};
      updated.sections[sectionIndex][field] = value;
      setPrivacyPolicy(updated);
    } else {
      const updated = {...termsOfService};
      updated.sections[sectionIndex][field] = value;
      setTermsOfService(updated);
    }
  };

  if (loading) {
    return <div className="text-center text-white/50 py-8">Loading...</div>;
  }

  // Full page content editor
  if (activeEditPage) {
    const pageData = activeEditPage === 'privacy' ? privacyPolicy : termsOfService;
    const setPageData = activeEditPage === 'privacy' ? setPrivacyPolicy : setTermsOfService;
    const pageName = activeEditPage === 'privacy' ? 'privacy_policy' : 'terms_of_service';
    const pageTitle = activeEditPage === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveEditPage(null)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
            <h3 className="text-lg text-white">Edit {pageTitle} Content</h3>
          </div>
          <Button 
            onClick={() => saveSettings(pageName, pageData)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Company Info */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <h4 className="text-white font-medium mb-4">Company Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm block mb-2">Company Name (Full)</label>
              <Input 
                value={pageData?.company_name || ''}
                onChange={(e) => setPageData({...pageData, company_name: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Black Star PI Ltd"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-2">Company Name (Short)</label>
              <Input 
                value={pageData?.company_short || ''}
                onChange={(e) => setPageData({...pageData, company_short: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Black Star PI"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-2">Contact Email</label>
              <Input 
                value={pageData?.contact_email || ''}
                onChange={(e) => setPageData({...pageData, contact_email: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
                placeholder="info@black-starpi.com"
              />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-2">Last Updated</label>
              <Input 
                type="date"
                value={pageData?.last_updated ? pageData.last_updated.split('T')[0] : ''}
                onChange={(e) => setPageData({...pageData, last_updated: e.target.value})}
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          <h4 className="text-white font-medium">Content Sections</h4>
          {pageData?.sections?.map((section, idx) => (
            <div key={section.id || idx} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
              <button
                onClick={() => setEditingSectionIndex(editingSectionIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[#D4AF37] text-sm font-mono">{idx + 1}</span>
                  <span className="text-white">{section.title}</span>
                </div>
                <Edit2 className={`w-4 h-4 text-white/40 transition-transform ${editingSectionIndex === idx ? 'rotate-45' : ''}`} />
              </button>
              {editingSectionIndex === idx && (
                <div className="p-4 pt-0 space-y-4 border-t border-white/10">
                  <div>
                    <label className="text-white/50 text-sm block mb-2">Section Title</label>
                    <Input 
                      value={section.title}
                      onChange={(e) => updateSection(activeEditPage, idx, 'title', e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-white/50 text-sm block mb-2">Section Content</label>
                    <textarea 
                      value={section.content}
                      onChange={(e) => updateSection(activeEditPage, idx, 'content', e.target.value)}
                      className="w-full bg-white/5 border border-white/10 text-white rounded-md p-3 min-h-[150px] resize-y"
                      placeholder="Enter section content..."
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg text-white mb-2">Legal Pages Management</h3>
        <p className="text-white/50 text-sm">Enable, disable, and edit content for legal pages.</p>
      </div>

      {/* Privacy Policy */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Privacy Policy</h4>
              <p className="text-white/40 text-sm">/privacy-policy &bull; {privacyPolicy?.sections?.length || 0} sections</p>
            </div>
          </div>
          <button
            onClick={() => setPrivacyPolicy({...privacyPolicy, enabled: !privacyPolicy?.enabled})}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              privacyPolicy?.enabled ? 'bg-green-500' : 'bg-white/20'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              privacyPolicy?.enabled ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center gap-3 mt-4">
          <Button 
            onClick={() => setActiveEditPage('privacy')}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/10"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit Content
          </Button>
          <Button 
            onClick={() => saveSettings('privacy_policy', privacyPolicy)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
          <a 
            href="/privacy-policy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#D4AF37] text-sm hover:underline ml-auto"
          >
            View Page &rarr;
          </a>
        </div>
        
        <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${
          privacyPolicy?.enabled 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {privacyPolicy?.enabled ? (
            <><CheckCircle className="w-3 h-3" /> Enabled</>
          ) : (
            <><AlertCircle className="w-3 h-3" /> Disabled</>
          )}
        </div>
      </div>

      {/* Terms of Service */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h4 className="text-white font-medium">Terms of Service</h4>
              <p className="text-white/40 text-sm">/terms-of-service &bull; {termsOfService?.sections?.length || 0} sections</p>
            </div>
          </div>
          <button
            onClick={() => setTermsOfService({...termsOfService, enabled: !termsOfService?.enabled})}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              termsOfService?.enabled ? 'bg-green-500' : 'bg-white/20'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              termsOfService?.enabled ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
        
        <div className="flex items-center gap-3 mt-4">
          <Button 
            onClick={() => setActiveEditPage('terms')}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/10"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit Content
          </Button>
          <Button 
            onClick={() => saveSettings('terms_of_service', termsOfService)}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" /> Save Settings
          </Button>
          <a 
            href="/terms-of-service" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#D4AF37] text-sm hover:underline ml-auto"
          >
            View Page &rarr;
          </a>
        </div>
        
        <div className={`mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs ${
          termsOfService?.enabled 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {termsOfService?.enabled ? (
            <><CheckCircle className="w-3 h-3" /> Enabled</>
          ) : (
            <><AlertCircle className="w-3 h-3" /> Disabled</>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#D4AF37] text-sm font-medium">Page Management</p>
            <p className="text-white/50 text-sm mt-1">
              Click "Edit Content" to modify page text, company info, and section content.
              Toggle switches to enable/disable pages. Disabled pages show "Page unavailable" to visitors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
